import React, { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  type: 'ember' | 'spark' | 'speck' | 'ash';
}
export function BurnTransition() {
  const {
    animState,
    origin
  } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const frameRef = useRef<number>(0);
  const isActive = animState !== 'idle';
  useEffect(() => {
    if (!isActive) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ox = origin.x / 100 * canvas.width;
    const oy = origin.y / 100 * canvas.height;
    const maxDist = Math.sqrt(canvas.width ** 2 + canvas.height ** 2);
    frameRef.current = 0;
    const TOTAL_FRAMES = 72; // ~1.2s at 60fps
    // ── Particles ──
    const particles: Particle[] = [];
    // Embers — slow, glowing, drift upward
    for (let i = 0; i < 70; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 4;
      particles.push({
        x: ox,
        y: oy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1 - Math.random() * 2,
        life: 0,
        maxLife: 60 + Math.random() * 55,
        size: 2.5 + Math.random() * 3.5,
        type: 'ember'
      });
    }
    // Fast sparks with trails
    for (let i = 0; i < 110; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 5 + Math.random() * 11;
      particles.push({
        x: ox,
        y: oy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1.5 - Math.random() * 3.5,
        life: 0,
        maxLife: 18 + Math.random() * 28,
        size: 0.8 + Math.random() * 2,
        type: 'spark'
      });
    }
    // Micro specks
    for (let i = 0; i < 150; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 3 + Math.random() * 14;
      particles.push({
        x: ox,
        y: oy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - Math.random() * 5,
        life: 0,
        maxLife: 10 + Math.random() * 20,
        size: 0.4 + Math.random() * 1,
        type: 'speck'
      });
    }
    // Ash particles — appear later, drift slowly upward
    for (let i = 0; i < 40; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.5 + Math.random() * 2;
      particles.push({
        x: ox + (Math.random() - 0.5) * 200,
        y: oy + (Math.random() - 0.5) * 200,
        vx: Math.cos(angle) * speed,
        vy: -0.3 - Math.random() * 1.2,
        life: -Math.floor(Math.random() * 20),
        maxLife: 50 + Math.random() * 40,
        size: 1.5 + Math.random() * 3,
        type: 'ash'
      });
    }
    const draw = () => {
      frameRef.current++;
      const f = frameRef.current;
      const progress = Math.min(f / TOTAL_FRAMES, 1);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // ── Phase 1: Fire spreads (0 → 0.45) ──
      // ── Phase 2: Full char coverage (0.45 → 0.6) ──
      // ── Phase 3: Ash/smoke fades (0.6 → 1.0) ──
      // Fire spread radius
      const fireRadius = maxDist * 1.15 * Math.min(progress / 0.45, 1);
      // Main fire glow — organic, not perfectly circular
      if (progress < 0.75) {
        const fireAlpha = progress < 0.45 ? Math.min(f / 6, 1) * 0.82 : Math.max(0, 1 - (progress - 0.45) / 0.3) * 0.82;
        const fire = ctx.createRadialGradient(ox, oy, 0, ox, oy, fireRadius);
        fire.addColorStop(0, `rgba(255,200,80,${fireAlpha * 0.9})`);
        fire.addColorStop(0.08, `rgba(255,100,10,${fireAlpha})`);
        fire.addColorStop(0.25, `rgba(200,40,0,${fireAlpha * 0.85})`);
        fire.addColorStop(0.5, `rgba(80,12,0,${fireAlpha * 0.6})`);
        fire.addColorStop(0.75, `rgba(20,4,0,${fireAlpha * 0.35})`);
        fire.addColorStop(1, `rgba(0,0,0,0)`);
        ctx.fillStyle = fire;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      // Char/ash overlay — dark coverage that peaks at phase 2
      if (progress > 0.2 && progress < 0.85) {
        const charProgress = Math.min((progress - 0.2) / 0.25, 1);
        const charFade = progress > 0.55 ? Math.max(0, 1 - (progress - 0.55) / 0.3) : 1;
        const charAlpha = charProgress * charFade * 0.78;
        const charR = maxDist * 1.2 * Math.min((progress - 0.2) / 0.3, 1);
        const char = ctx.createRadialGradient(ox, oy, 0, ox, oy, charR);
        char.addColorStop(0, `rgba(8,3,0,${charAlpha})`);
        char.addColorStop(0.3, `rgba(15,5,0,${charAlpha * 0.9})`);
        char.addColorStop(0.65, `rgba(5,2,0,${charAlpha * 0.5})`);
        char.addColorStop(1, `rgba(0,0,0,0)`);
        ctx.fillStyle = char;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      // Char edge ring — the burning frontier
      if (progress < 0.55) {
        const edgeInner = fireRadius * 0.78;
        const edgeOuter = fireRadius * 1.12;
        const edgeAlpha = Math.min(f / 8, 1) * Math.max(0, 1 - (progress - 0.3) / 0.25) * 0.9;
        const edge = ctx.createRadialGradient(ox, oy, edgeInner, ox, oy, edgeOuter);
        edge.addColorStop(0, `rgba(0,0,0,0)`);
        edge.addColorStop(0.4, `rgba(255,60,0,${edgeAlpha * 0.6})`);
        edge.addColorStop(0.7, `rgba(10,3,0,${edgeAlpha})`);
        edge.addColorStop(1, `rgba(0,0,0,0)`);
        ctx.fillStyle = edge;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      // ── Burst flash at origin (first 6 frames) ──
      if (f < 7) {
        const flashAlpha = (1 - f / 7) * 0.95;
        const flashR = 20 + f * 18;
        const flash = ctx.createRadialGradient(ox, oy, 0, ox, oy, flashR);
        flash.addColorStop(0, `rgba(255,250,200,${flashAlpha})`);
        flash.addColorStop(0.3, `rgba(255,150,30,${flashAlpha * 0.85})`);
        flash.addColorStop(0.7, `rgba(200,50,0,${flashAlpha * 0.4})`);
        flash.addColorStop(1, `rgba(0,0,0,0)`);
        ctx.fillStyle = flash;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      // ── Particles ──
      particles.forEach(p => {
        if (p.life >= p.maxLife) return;
        p.life++;
        if (p.life <= 0) return; // staggered ash start
        const friction = p.type === 'ember' ? 0.975 : p.type === 'spark' ? 0.92 : p.type === 'ash' ? 0.99 : 0.88;
        p.vx *= friction;
        p.vy *= friction;
        if (p.type !== 'ash') p.vy += 0.09; // gravity
        else p.vy -= 0.01; // ash floats up
        p.x += p.vx;
        p.y += p.vy;
        const lr = Math.max(0, p.life) / p.maxLife;
        const alpha = lr < 0.12 ? lr / 0.12 : Math.max(0, 1 - (lr - 0.12) / 0.88);
        const size = p.size * (1 - lr * 0.5);
        if (p.type === 'ember') {
          const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 3.5);
          g.addColorStop(0, `rgba(255,245,180,${alpha})`);
          g.addColorStop(0.2, `rgba(255,150,20,${alpha * 0.95})`);
          g.addColorStop(0.55, `rgba(210,55,0,${alpha * 0.65})`);
          g.addColorStop(1, `rgba(60,8,0,0)`);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 3.5, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === 'spark') {
          const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
          const trailLen = Math.min(spd * 3, 16);
          const nx = spd > 0 ? p.vx / spd : 0;
          const ny = spd > 0 ? p.vy / spd : 0;
          const grad = ctx.createLinearGradient(p.x + nx * trailLen, p.y + ny * trailLen, p.x, p.y);
          grad.addColorStop(0, `rgba(255,180,60,0)`);
          grad.addColorStop(1, `rgba(255,220,100,${alpha})`);
          ctx.strokeStyle = grad;
          ctx.lineWidth = size * 1.4;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(p.x + nx * trailLen, p.y + ny * trailLen);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          ctx.fillStyle = `rgba(255,245,200,${alpha})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 0.9, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === 'ash') {
          // Soft grey ash flakes
          const ashAlpha = alpha * 0.55;
          ctx.fillStyle = `rgba(180,160,140,${ashAlpha})`;
          ctx.beginPath();
          ctx.ellipse(p.x, p.y, size * 1.8, size * 0.9, p.vx * 0.3, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // Micro speck
          ctx.fillStyle = `rgba(255,190,70,${alpha * 0.85})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      if (f < TOTAL_FRAMES) {
        animRef.current = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [isActive]);
  if (!isActive) return null;
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none" style={{
    zIndex: 9999
  }} data-id="element-1" />;
}